import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import type { CuentaTravesia } from '#components/mobile/travesia/escribir';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { CampoImporte } from '#components/mobile/ui/CampoImporte';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { aEscrito } from '#components/mobile/ui/TecladoImporte';
import { color } from '#components/mobile/ui/tokens';

import {
  estiloCampo,
  estiloOpcion,
  estiloOpciones,
  Etiqueta,
  HojaTravesia,
  leerImporte,
  Marca,
  TituloHoja,
} from './comun';

export type TipoCuentas = 'banco' | 'efectivo' | 'dos';

/** Cómo se dice lo que hay en el banco: con el extracto o a mano. */
export type ModoSaldo = 'extracto' | 'escribir';

/** Lo que pide «Elegir el archivo»: la cuenta del banco y, si hay, el efectivo. */
export type PeticionExtracto = {
  nombre: string;
  efectivo: CuentaTravesia | null;
};

/**
 * Cuentas que crea el paso 5 según lo elegido. Sin nombre, «Cuenta del
 * banco»; sin saldo, 0 €.
 */
export function cuentasElegidas(
  tipo: TipoCuentas,
  campos: { nombre: string; saldoBanco: string; saldoEfectivo: string },
  nombres: { banco: string; efectivo: string },
): CuentaTravesia[] {
  const res: CuentaTravesia[] = [];
  if (tipo !== 'efectivo') {
    res.push({
      nombre: campos.nombre.trim() || nombres.banco,
      saldo: leerImporte(campos.saldoBanco) ?? 0,
    });
  }
  if (tipo !== 'banco') {
    res.push({
      nombre: nombres.efectivo,
      saldo: leerImporte(campos.saldoEfectivo) ?? 0,
    });
  }
  return res;
}

type HojaCuentasProps = {
  ocupado?: boolean;
  /** La hoja baja mientras la cuenta aparece en Cuentas, detrás. */
  bajando?: boolean;
  onAnadir: (cuentas: CuentaTravesia[]) => void;
  /** «Traer el extracto»: elegir el archivo del banco. */
  onExtracto: (peticion: PeticionExtracto) => void;
  /** Al volver de un extracto que no se pudo leer: «Escribo el saldo». */
  modoInicial?: ModoSaldo;
};

/**
 * Paso 5 · «¿Dónde tienes tu dinero hoy?» sobre la pantalla Cuentas real.
 * Para el banco, Gavi propone traer el extracto (saca el saldo de hoy y la
 * historia) o escribir el saldo, como antes.
 */
export function HojaCuentas({
  ocupado,
  bajando,
  onAnadir,
  onExtracto,
  modoInicial = 'extracto',
}: HojaCuentasProps) {
  const { t } = useTranslation();
  const [tipo, setTipo] = useState<TipoCuentas>('banco');
  const [modo, setModo] = useState<ModoSaldo>(modoInicial);
  const [nombre, setNombre] = useState('');
  const [saldoBanco, setSaldoBanco] = useState('');
  const [saldoEfectivo, setSaldoEfectivo] = useState('');
  const [gavi, setGavi] = useState<{
    texto: string | null;
    estado: GaviEstado;
    clave: number;
  }>({ texto: null, estado: 'vuela', clave: 0 });

  function alEscribirSaldo(centimos: number | null) {
    if (centimos === 0) {
      setGavi(g => ({
        texto: t('Zero? Starting from zero is still starting.'),
        estado: 'ojos-plato',
        clave: g.clave + 1,
      }));
    }
  }

  const campoSaldo = (
    id: string,
    etiqueta: string,
    valor: string,
    cambiar: (v: string) => void,
  ) => (
    <div style={{ marginTop: 12 }}>
      <Etiqueta htmlFor={id}>{etiqueta}</Etiqueta>
      <CampoImporte
        id={id}
        aria-label={etiqueta}
        data-testid={id}
        valor={leerImporte(valor)}
        opcional
        conSigno
        onCambiar={centimos => {
          cambiar(
            centimos == null
              ? ''
              : `${centimos < 0 ? '-' : ''}${aEscrito(Math.abs(centimos))}`,
          );
          alEscribirSaldo(centimos);
        }}
        style={{ ...estiloCampo, justifyContent: 'flex-start' }}
        estiloTexto={{ fontWeight: 800, fontSize: 20 }}
      />
    </div>
  );

  const saldo = t('Current balance');
  const conBanco = tipo !== 'efectivo';
  const conExtracto = conBanco && modo === 'extracto';
  const nombres = { banco: t('Bank account'), efectivo: t('Cash') };

  function continuar() {
    if (conExtracto) {
      const efectivo =
        tipo === 'dos'
          ? cuentasElegidas(
              'efectivo',
              { nombre: '', saldoBanco: '', saldoEfectivo },
              nombres,
            )[0]
          : null;
      onExtracto({ nombre: nombre.trim() || nombres.banco, efectivo });
      return;
    }
    onAnadir(
      cuentasElegidas(tipo, { nombre, saldoBanco, saldoEfectivo }, nombres),
    );
  }

  const modos: { valor: ModoSaldo; titulo: string; texto: string }[] = [
    {
      valor: 'extracto',
      titulo: t('Bring the bank statement'),
      texto: t(
        "I'll work out today's balance and your history. I recommend it.",
      ),
    },
    {
      valor: 'escribir',
      titulo: t('I type the balance'),
      texto: t("Just today's figure. You start without history."),
    },
  ];

  return (
    <HojaTravesia
      aria-label={t('Where is your money today?')}
      data-testid="hoja-cuentas"
      bajando={bajando}
      cabeza={
        <Bocadillo
          variante="hoja"
          texto={
            gavi.texto ??
            (conExtracto
              ? t(
                  "Give me your bank statement and I'll work out today's balance and learn from what you spent before.",
                )
              : t(
                  'Enter what your bank says today. Nobody checks it. Well, I do.',
                ))
          }
          estado={gavi.estado}
          clave={gavi.clave}
        />
      }
      pie={
        <Boton
          bloque
          isDisabled={ocupado}
          data-testid="travesia-anadir-cuenta"
          onPress={continuar}
        >
          {conExtracto ? (
            <Trans>Choose the file</Trans>
          ) : (
            <Trans>Add account</Trans>
          )}
        </Boton>
      }
    >
      <TituloHoja>
        <Trans>Where is your money today?</Trans>
      </TituloHoja>
      <Segmentado
        etiqueta={t('Where is your money today?')}
        testId="tipo-cuenta"
        estirar
        alto={40}
        valor={tipo}
        onChange={setTipo}
        opciones={[
          { valor: 'banco', texto: t('Bank') },
          { valor: 'efectivo', texto: t('Cash') },
          { valor: 'dos', texto: t('Both') },
        ]}
        style={{ borderRadius: 14 }}
      />
      {tipo !== 'efectivo' && (
        <div style={{ marginTop: 12 }}>
          <Etiqueta htmlFor="travesia-nombre-cuenta">
            <Trans>Account name</Trans>
          </Etiqueta>
          <input
            id="travesia-nombre-cuenta"
            data-testid="travesia-nombre-cuenta"
            maxLength={30}
            autoComplete="off"
            placeholder={t('e.g. CaixaBank, Cash…')}
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            style={estiloCampo}
          />
        </div>
      )}
      {conBanco && (
        <div style={{ marginTop: 12 }}>
          <Etiqueta>
            <Trans>How do you tell me what you have?</Trans>
          </Etiqueta>
          <div style={estiloOpciones}>
            {modos.map((m, i) => (
              <button
                type="button"
                key={m.valor}
                aria-pressed={modo === m.valor}
                data-testid={`modo-saldo-${m.valor}`}
                style={estiloOpcion(i === 0)}
                onClick={() => setModo(m.valor)}
              >
                <IconoCaja
                  tono="acento"
                  size={36}
                  icono={m.valor === 'extracto' ? 'download' : 'pencil'}
                />
                <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
                  <span style={{ display: 'block' }}>{m.titulo}</span>
                  <span
                    style={{
                      display: 'block',
                      fontWeight: 500,
                      fontSize: 13,
                      color: color.fg2,
                    }}
                  >
                    {m.texto}
                  </span>
                </span>
                <Marca marcada={modo === m.valor} />
              </button>
            ))}
          </div>
        </div>
      )}
      {conBanco &&
        !conExtracto &&
        campoSaldo(
          'travesia-saldo-banco',
          tipo === 'dos' ? `${saldo} · ${t('Bank')}` : saldo,
          saldoBanco,
          setSaldoBanco,
        )}
      {tipo !== 'banco' &&
        campoSaldo(
          'travesia-saldo-efectivo',
          tipo === 'dos' ? `${saldo} · ${t('Cash')}` : saldo,
          saldoEfectivo,
          setSaldoEfectivo,
        )}
    </HojaTravesia>
  );
}
